import React, { createContext, useContext, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useCurrentClient } from '@/hooks/useCurrentClient';
import type {
  FinanceAccount,
  FinanceCategory,
  FinanceCostCenter,
  FinanceCounterparty,
  FinanceTransaction,
  FilterState,
  ChartDataPoint,
  DashboardSummary,
  TransactionType,
  TransactionStatus,
  RecurrenceType,
  AccountType,
  CounterpartyKind,
  RecurrenceActionScope,
  ApprovalStatus,
  CategoryAllocation,
  CostCenterAllocation,
} from '@/types/finance';
import {
  format,
  startOfMonth,
  endOfMonth,
  eachMonthOfInterval,
  eachDayOfInterval,
  startOfYear,
  endOfYear,
  isSameMonth,
  isSameDay,
  addDays,
  addWeeks,
  addMonths,
  addYears,
} from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'sonner';
import type { Database } from '@/types/database';
import { normalizeName } from '@/features/transactions/import/parsers/contaAzulParser';
import { usePersistedClientState } from '@/hooks/usePersistedClientState';

type AccountUpdate = Database['public']['Tables']['accounts']['Update'];
type CategoryUpdate = Database['public']['Tables']['categories']['Update'];
type CostCenterUpdate = Database['public']['Tables']['cost_centers']['Update'];
type CounterpartyUpdate = Database['public']['Tables']['counterparties']['Update'];
type TransactionUpdate = Database['public']['Tables']['transactions']['Update'];

interface FinanceContextType {
  accounts: FinanceAccount[];
  categories: FinanceCategory[];
  transactions: FinanceTransaction[];
  costCenters: FinanceCostCenter[];
  counterparties: FinanceCounterparty[];
  filters: FilterState;
  isLoading: boolean;
  isRefetching: boolean;
  setFilters: (filters: FilterState) => void;
  addTransaction: (t: Omit<FinanceTransaction, 'id' | 'createdAt' | 'updatedAt'>) => void;
  updateTransaction: (id: string, t: Partial<FinanceTransaction>) => void;
  deleteTransaction: (id: string) => void;
  deleteRecurringTransaction: (
    id: string,
    scope: RecurrenceActionScope,
    t: FinanceTransaction,
  ) => void;
  updateRecurringTransaction: (
    id: string,
    updates: Partial<FinanceTransaction>,
    scope: RecurrenceActionScope,
    t: FinanceTransaction,
  ) => void;
  markAsCompleted: (
    id: string,
    opts?: { paymentDate?: Date; interest?: number; fine?: number },
  ) => void;
  addAccount: (a: Omit<FinanceAccount, 'id' | 'createdAt'>) => void;
  updateAccount: (id: string, a: Partial<FinanceAccount>) => void;
  deleteAccount: (id: string) => void;
  addCategory: (c: Omit<FinanceCategory, 'id'>) => void;
  updateCategory: (id: string, c: Partial<FinanceCategory>) => void;
  deleteCategory: (id: string) => void;
  addCostCenter: (cc: Omit<FinanceCostCenter, 'id' | 'createdAt'>) => void;
  updateCostCenter: (id: string, cc: Partial<FinanceCostCenter>) => void;
  deleteCostCenter: (id: string) => void;
  addCounterparty: (
    cp: Omit<FinanceCounterparty, 'id' | 'createdAt' | 'updatedAt'>,
  ) => Promise<FinanceCounterparty>;
  updateCounterparty: (id: string, cp: Partial<FinanceCounterparty>) => void;
  deleteCounterparty: (id: string) => void;
  getChartData: () => ChartDataPoint[];
  getDashboardSummary: () => DashboardSummary;
  getFilteredTransactions: () => FinanceTransaction[];
  getCategoryById: (id: string) => FinanceCategory | undefined;
  getAccountById: (id: string) => FinanceAccount | undefined;
  getCostCenterById: (id: string) => FinanceCostCenter | undefined;
  getCategoryByName: (name: string, type: TransactionType) => FinanceCategory | undefined;
  getCostCenterByName: (name: string) => FinanceCostCenter | undefined;
  importBatch: (input: ImportBatchInput) => Promise<ImportBatchResult>;
  reconcileMatches: (matches: ReconcileMatchInput[]) => Promise<ReconcileMatchesResult>;
  approveTransactions: (ids: string[], approvedByUserId: string) => Promise<number>;
  rejectTransactions: (ids: string[], reason: string) => Promise<number>;
  bulkMarkCompleted: (ids: string[]) => Promise<number>;
  bulkMarkPending: (ids: string[]) => Promise<number>;
  bulkUpdateCostCenter: (ids: string[], costCenterId: string | null) => Promise<number>;
  bulkUpdateCounterparty: (ids: string[], counterpartyId: string | null) => Promise<number>;
  bulkDelete: (ids: string[]) => Promise<number>;
  closeCreditCardInvoice: (input: CloseInvoiceInput) => Promise<CloseInvoiceResult>;
  refetchAllData: () => Promise<void>;
}

export interface ImportBatchInput {
  categoriesToCreate: { name: string; type: TransactionType; color: string }[];
  costCentersToCreate: { name: string; color: string }[];
  transactions: Array<{
    type: TransactionType;
    description: string;
    amount: number;
    dueDate: Date;
    paymentDate?: Date;
    accountId: string;
    /** Apenas para `type='transfer'`: conta destino. */
    destinationAccountId?: string;
    status: 'pending' | 'completed';
    categoryName?: string;
    costCenterName?: string;
    existingCategoryId?: string;
    existingCostCenterId?: string;
    notes?: string;
    /** ID externo (FITID do OFX) para evitar duplicação. */
    externalId?: string;
  }>;
}

export interface ImportBatchResult {
  categoriesCreated: number;
  costCentersCreated: number;
  transactionsCreated: number;
  transactionsSkippedDuplicate: number;
  errors: { line?: number; reason: string }[];
}

export interface ReconcileMatchInput {
  transactionId: string;
  paymentDate: string; // YYYY-MM-DD
  paidAmount: number;
}

export interface ReconcileMatchesResult {
  reconciled: number;
  errors: { transactionId?: string; reason: string }[];
}

export interface CloseInvoiceInput {
  cardAccountId: string;
  periodStart: string; // YYYY-MM-DD
  periodEnd: string; // YYYY-MM-DD
  paymentAccountId: string;
  paymentDate: string; // YYYY-MM-DD
}

export interface CloseInvoiceResult {
  closedCount: number;
  totalAmount: number;
  transferId: string;
}

const FinanceContext = createContext<FinanceContextType | undefined>(undefined);

const parseLocalDate = (dateStr: string): Date => {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year!, month! - 1, day!);
};

type DbRow = Record<string, unknown>;

const mapDbAccount = (row: DbRow): FinanceAccount => {
  const closingDay = row['closing_day'] as number | null;
  const dueDay = row['due_day'] as number | null;
  const creditLimit = row['credit_limit'] as string | number | null;
  return {
    id: row['id'] as string,
    name: row['name'] as string,
    type: row['type'] as AccountType,
    initialBalance: Number(row['initial_balance']),
    currentBalance: Number(row['current_balance']),
    color: row['color'] as string,
    isActive: row['is_active'] as boolean,
    createdAt: new Date(row['created_at'] as string),
    ...(closingDay !== null && { closingDay }),
    ...(dueDay !== null && { dueDay }),
    ...(creditLimit !== null && { creditLimit: Number(creditLimit) }),
  };
};

const mapDbCategory = (row: DbRow): FinanceCategory => ({
  id: row['id'] as string,
  name: row['name'] as string,
  type: row['type'] as TransactionType,
  color: row['color'] as string,
  icon: (row['icon'] as string | null) ?? 'Circle',
});

const mapDbCostCenter = (row: DbRow): FinanceCostCenter => {
  const description = row['description'] as string | null;
  return {
    id: row['id'] as string,
    name: row['name'] as string,
    ...(description !== null && { description }),
    color: row['color'] as string,
    isActive: row['is_active'] as boolean,
    createdAt: new Date(row['created_at'] as string),
  };
};

const mapDbCounterparty = (row: DbRow): FinanceCounterparty => {
  const document = row['document'] as string | null;
  const email = row['email'] as string | null;
  const phone = row['phone'] as string | null;
  const notes = row['notes'] as string | null;
  return {
    id: row['id'] as string,
    name: row['name'] as string,
    kind: row['kind'] as CounterpartyKind,
    ...(document !== null && { document }),
    ...(email !== null && { email }),
    ...(phone !== null && { phone }),
    ...(notes !== null && { notes }),
    createdAt: new Date(row['created_at'] as string),
    updatedAt: new Date(row['updated_at'] as string),
  };
};

const mapDbTransaction = (row: DbRow): FinanceTransaction => {
  const paymentDate = row['payment_date'] as string | null;
  const destinationAccountId = row['destination_account_id'] as string | null;
  const costCenterId = row['cost_center_id'] as string | null;
  const counterpartyId = row['counterparty_id'] as string | null;
  const interestRaw = row['interest'] as string | number | null | undefined;
  const fineRaw = row['fine'] as string | number | null | undefined;
  const interest = interestRaw == null ? 0 : Number(interestRaw);
  const fine = fineRaw == null ? 0 : Number(fineRaw);
  const recurrenceGroupId = row['recurrence_group_id'] as string | null;
  const notes = row['notes'] as string | null;
  const numberOfRepetitions = row['number_of_repetitions'] as number | null;
  const approvedAt = row['approved_at'] as string | null;
  const approvedByUserId = row['approved_by_user_id'] as string | null;
  const rejectionReason = row['rejection_reason'] as string | null;
  const externalId = row['external_id'] as string | null;
  const splitsRaw = row['category_splits'] as
    | Array<{ id: string; category_id: string; amount: string | number; order_index: number }>
    | undefined;
  const categorySplits =
    splitsRaw && splitsRaw.length > 0
      ? [...splitsRaw]
          .sort((a, b) => a.order_index - b.order_index)
          .map((s) => ({
            categoryId: s.category_id,
            amount: Number(s.amount),
            orderIndex: s.order_index,
          }))
      : undefined;
  const ccSplitsRaw = row['cost_center_splits'] as
    | Array<{ id: string; cost_center_id: string; amount: string | number; order_index: number }>
    | undefined;
  const costCenterSplits =
    ccSplitsRaw && ccSplitsRaw.length > 0
      ? [...ccSplitsRaw]
          .sort((a, b) => a.order_index - b.order_index)
          .map((s) => ({
            costCenterId: s.cost_center_id,
            amount: Number(s.amount),
            orderIndex: s.order_index,
          }))
      : undefined;

  return {
    id: row['id'] as string,
    type: row['type'] as TransactionType,
    description: row['description'] as string,
    amount: Number(row['amount']),
    dueDate: parseLocalDate(row['date'] as string),
    ...(paymentDate !== null && { paymentDate: parseLocalDate(paymentDate) }),
    categoryId: (row['category_id'] as string | null) ?? '',
    accountId: row['account_id'] as string,
    ...(destinationAccountId !== null && { destinationAccountId }),
    ...(costCenterId !== null && { costCenterId }),
    ...(counterpartyId !== null && { counterpartyId }),
    ...(interest > 0 && { interest }),
    ...(fine > 0 && { fine }),
    status: row['status'] as TransactionStatus,
    recurrence: ((row['recurrence_type'] as string | null) ?? 'none') as RecurrenceType,
    ...(numberOfRepetitions !== null && { numberOfRepetitions }),
    ...(recurrenceGroupId !== null && { recurrenceGroupId }),
    ...(notes !== null && { notes }),
    approvalStatus: ((row['approval_status'] as string | null) ?? 'not_required') as ApprovalStatus,
    ...(approvedAt !== null && { approvedAt: new Date(approvedAt) }),
    ...(approvedByUserId !== null && { approvedByUserId }),
    ...(rejectionReason !== null && { rejectionReason }),
    ...(externalId !== null && { externalId }),
    ...(categorySplits && { categorySplits }),
    ...(costCenterSplits && { costCenterSplits }),
    createdAt: new Date(row['created_at'] as string),
    updatedAt: new Date(row['updated_at'] as string),
  };
};

/**
 * Retorna allocations efetivas de uma transação para agregações.
 * - Com split: cada row de categorySplits vira uma allocation.
 * - Sem split: uma allocation virtual (categoryId, amount, orderIndex=0).
 *   Se a transação não tem categoria, retorna array vazio (não soma em DRE).
 */
// eslint-disable-next-line react-refresh/only-export-components -- helper puro reaproveitado em agregações; manter aqui evita dependência de React nos testes
export function getAllocations(tx: FinanceTransaction): CategoryAllocation[] {
  if (tx.categorySplits && tx.categorySplits.length > 0) {
    return tx.categorySplits;
  }
  if (!tx.categoryId) return [];
  return [{ categoryId: tx.categoryId, amount: tx.amount, orderIndex: 0 }];
}

/**
 * Retorna allocations efetivas de centro de custo de uma transação.
 * - Com split: cada row de costCenterSplits vira uma allocation.
 * - Sem split: uma allocation virtual (costCenterId, amount, orderIndex=0).
 *   Se a transação não tem centro de custo, retorna array vazio.
 */
// eslint-disable-next-line react-refresh/only-export-components -- helper puro reaproveitado em agregações; manter aqui evita dependência de React nos testes
export function getCostCenterAllocations(tx: FinanceTransaction): CostCenterAllocation[] {
  if (tx.costCenterSplits && tx.costCenterSplits.length > 0) {
    return tx.costCenterSplits;
  }
  if (!tx.costCenterId) return [];
  return [{ costCenterId: tx.costCenterId, amount: tx.amount, orderIndex: 0 }];
}

const getNextRecurrenceDate = (date: Date, recurrence: RecurrenceType): Date => {
  switch (recurrence) {
    case 'daily':
      return addDays(date, 1);
    case 'weekly':
      return addWeeks(date, 1);
    case 'biweekly':
      return addWeeks(date, 2);
    case 'monthly':
      return addMonths(date, 1);
    case 'yearly':
      return addYears(date, 1);
    default:
      return date;
  }
};

export function FinanceProvider({ children }: { children: React.ReactNode }) {
  const { clientId } = useCurrentClient();
  const queryClient = useQueryClient();

  const [filters, setFilters] = usePersistedClientState<FilterState>('globalFilters', {
    periodMode: 'monthly',
    year: new Date().getFullYear(),
    month: new Date().getMonth(),
    accountId: 'all',
    costCenterId: 'all',
    viewMode: 'all',
  });

  const {
    data: accounts = [],
    isLoading: accountsLoading,
    isRefetching: accountsRefetching,
  } = useQuery({
    queryKey: ['accounts', clientId],
    queryFn: async () => {
      if (!clientId) return [];
      const { data, error } = await supabase
        .from('accounts')
        .select('*')
        .eq('client_id', clientId)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return (data as DbRow[]).map(mapDbAccount);
    },
    enabled: !!clientId,
  });

  const {
    data: categories = [],
    isLoading: categoriesLoading,
    isRefetching: categoriesRefetching,
  } = useQuery({
    queryKey: ['categories', clientId],
    queryFn: async () => {
      if (!clientId) return [];
      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .eq('client_id', clientId)
        .order('name', { ascending: true });
      if (error) throw error;
      return (data as DbRow[]).map(mapDbCategory);
    },
    enabled: !!clientId,
  });

  const {
    data: costCenters = [],
    isLoading: costCentersLoading,
    isRefetching: costCentersRefetching,
  } = useQuery({
    queryKey: ['cost_centers', clientId],
    queryFn: async () => {
      if (!clientId) return [];
      const { data, error } = await supabase
        .from('cost_centers')
        .select('*')
        .eq('client_id', clientId)
        .order('name', { ascending: true });
      if (error) throw error;
      return (data as DbRow[]).map(mapDbCostCenter);
    },
    enabled: !!clientId,
  });

  const {
    data: counterparties = [],
    isLoading: counterpartiesLoading,
    isRefetching: counterpartiesRefetching,
  } = useQuery({
    queryKey: ['counterparties', clientId],
    queryFn: async () => {
      if (!clientId) return [];
      const { data, error } = await supabase
        .from('counterparties')
        .select('*')
        .eq('client_id', clientId)
        .is('deleted_at', null)
        .order('name', { ascending: true });
      if (error) throw error;
      return (data as DbRow[]).map(mapDbCounterparty);
    },
    enabled: !!clientId,
  });

  const {
    data: transactions = [],
    isLoading: transactionsLoading,
    isRefetching: transactionsRefetching,
  } = useQuery({
    queryKey: ['transactions', clientId, filters.year],
    queryFn: async () => {
      if (!clientId) return [];
      // O PostgREST do Supabase tem um teto rígido de linhas por response
      // (db-max-rows=1000 por default). O `.limit(10000)` do cliente é
      // ignorado pelo servidor — pegava só as 1000 mais recentes do ano e
      // descartava silenciosamente as mais antigas. Para clientes com >1000
      // lançamentos/ano (caso da Cris Miura, ~1640 em 2025), isso fazia
      // jan-abr desaparecerem da UI mesmo estando no banco.
      // Solução: paginar manualmente até esvaziar (ou bater no cap de segurança).
      const PAGE = 1000;
      const SAFETY_CAP = 50_000;
      const acc: DbRow[] = [];
      let from = 0;
      while (from < SAFETY_CAP) {
        const { data, error } = await supabase
          .from('transactions')
          .select(
            '*, category_splits:transaction_category_splits(*), cost_center_splits:transaction_cost_center_splits(*)',
          )
          .eq('client_id', clientId)
          .is('deleted_at', null)
          .gte('date', `${filters.year}-01-01`)
          .lte('date', `${filters.year}-12-31`)
          .order('date', { ascending: false })
          .range(from, from + PAGE - 1);
        if (error) throw error;
        const rows = (data ?? []) as DbRow[];
        acc.push(...rows);
        if (rows.length < PAGE) break;
        from += PAGE;
      }
      return acc.map(mapDbTransaction);
    },
    enabled: !!clientId,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  const isLoading =
    accountsLoading ||
    categoriesLoading ||
    costCentersLoading ||
    counterpartiesLoading ||
    transactionsLoading;
  const isRefetching =
    accountsRefetching ||
    categoriesRefetching ||
    costCentersRefetching ||
    counterpartiesRefetching ||
    transactionsRefetching;

  const refetchAllData = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['accounts', clientId] }),
      queryClient.invalidateQueries({ queryKey: ['categories', clientId] }),
      queryClient.invalidateQueries({ queryKey: ['cost_centers', clientId] }),
      queryClient.invalidateQueries({ queryKey: ['counterparties', clientId] }),
      queryClient.invalidateQueries({ queryKey: ['transactions', clientId, filters.year] }),
    ]);
  }, [clientId, queryClient, filters.year]);

  // Account mutations
  const addAccountMutation = useMutation({
    mutationFn: async (account: Omit<FinanceAccount, 'id' | 'createdAt'>) => {
      if (!clientId) throw new Error('Cliente não selecionado');
      const { error } = await supabase.from('accounts').insert({
        client_id: clientId,
        name: account.name,
        type: account.type,
        initial_balance: account.initialBalance,
        current_balance: account.currentBalance,
        color: account.color,
        is_active: account.isActive,
        closing_day: account.closingDay ?? null,
        due_day: account.dueDay ?? null,
        credit_limit: account.creditLimit ?? null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts', clientId] });
      toast.success('Conta criada!');
    },
    onError: (e: Error) => toast.error('Erro ao criar conta: ' + e.message),
  });

  const updateAccountMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<FinanceAccount> }) => {
      const dbUpdates: AccountUpdate = {};
      if (updates.name !== undefined) dbUpdates.name = updates.name;
      if (updates.type !== undefined) dbUpdates.type = updates.type;
      if (updates.initialBalance !== undefined) dbUpdates.initial_balance = updates.initialBalance;
      if (updates.currentBalance !== undefined) dbUpdates.current_balance = updates.currentBalance;
      if (updates.color !== undefined) dbUpdates.color = updates.color;
      if (updates.isActive !== undefined) dbUpdates.is_active = updates.isActive;
      if ('closingDay' in updates) dbUpdates.closing_day = updates.closingDay ?? null;
      if ('dueDay' in updates) dbUpdates.due_day = updates.dueDay ?? null;
      if ('creditLimit' in updates) dbUpdates.credit_limit = updates.creditLimit ?? null;
      const { error } = await supabase.from('accounts').update(dbUpdates).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts', clientId] });
      toast.success('Conta atualizada!');
    },
    onError: (e: Error) => toast.error('Erro ao atualizar conta: ' + e.message),
  });

  const deleteAccountMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('accounts')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['accounts', clientId] });
      toast.success('Conta excluída!');
    },
    onError: (e: Error) => toast.error('Erro ao excluir conta: ' + e.message),
  });

  // Category mutations
  const addCategoryMutation = useMutation({
    mutationFn: async (category: Omit<FinanceCategory, 'id'>) => {
      if (!clientId) throw new Error('Cliente não selecionado');
      const { error } = await supabase.from('categories').insert({
        client_id: clientId,
        name: category.name,
        type: category.type,
        color: category.color,
        icon: category.icon,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories', clientId] });
      toast.success('Categoria criada!');
    },
    onError: (e: Error) => toast.error('Erro ao criar categoria: ' + e.message),
  });

  const updateCategoryMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<FinanceCategory> }) => {
      const dbUpdates: CategoryUpdate = {};
      if (updates.name !== undefined) dbUpdates.name = updates.name;
      if (updates.type !== undefined) dbUpdates.type = updates.type;
      if (updates.color !== undefined) dbUpdates.color = updates.color;
      if (updates.icon !== undefined) dbUpdates.icon = updates.icon;
      const { error } = await supabase.from('categories').update(dbUpdates).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories', clientId] });
      toast.success('Categoria atualizada!');
    },
    onError: (e: Error) => toast.error('Erro ao atualizar categoria: ' + e.message),
  });

  const deleteCategoryMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('categories')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories', clientId] });
      toast.success('Categoria excluída!');
    },
    onError: (e: Error) => toast.error('Erro ao excluir categoria: ' + e.message),
  });

  // Cost center mutations
  const addCostCenterMutation = useMutation({
    mutationFn: async (cc: Omit<FinanceCostCenter, 'id' | 'createdAt'>) => {
      if (!clientId) throw new Error('Cliente não selecionado');
      const { error } = await supabase.from('cost_centers').insert({
        client_id: clientId,
        name: cc.name,
        description: cc.description ?? null,
        color: cc.color,
        is_active: cc.isActive,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cost_centers', clientId] });
      toast.success('Centro de custo criado!');
    },
    onError: (e: Error) => toast.error('Erro ao criar centro de custo: ' + e.message),
  });

  const updateCostCenterMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<FinanceCostCenter> }) => {
      const dbUpdates: CostCenterUpdate = {};
      if (updates.name !== undefined) dbUpdates.name = updates.name;
      if (updates.description !== undefined) dbUpdates.description = updates.description ?? null;
      if (updates.color !== undefined) dbUpdates.color = updates.color;
      if (updates.isActive !== undefined) dbUpdates.is_active = updates.isActive;
      const { error } = await supabase.from('cost_centers').update(dbUpdates).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cost_centers', clientId] });
      toast.success('Centro de custo atualizado!');
    },
    onError: (e: Error) => toast.error('Erro ao atualizar centro de custo: ' + e.message),
  });

  const deleteCostCenterMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('cost_centers')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cost_centers', clientId] });
      toast.success('Centro de custo excluído!');
    },
    onError: (e: Error) => toast.error('Erro ao excluir centro de custo: ' + e.message),
  });

  // Counterparty mutations
  const addCounterpartyMutation = useMutation({
    mutationFn: async (
      cp: Omit<FinanceCounterparty, 'id' | 'createdAt' | 'updatedAt'>,
    ): Promise<FinanceCounterparty> => {
      if (!clientId) throw new Error('Cliente não selecionado');
      const { data, error } = await supabase
        .from('counterparties')
        .insert({
          client_id: clientId,
          name: cp.name,
          kind: cp.kind,
          document: cp.document ?? null,
          email: cp.email ?? null,
          phone: cp.phone ?? null,
          notes: cp.notes ?? null,
        })
        .select('*')
        .single();
      if (error) throw error;
      return mapDbCounterparty(data as DbRow);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['counterparties', clientId] });
      toast.success('Contraparte criada!');
    },
    onError: (e: Error) => toast.error('Erro ao criar contraparte: ' + e.message),
  });

  const updateCounterpartyMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<FinanceCounterparty> }) => {
      const dbUpdates: CounterpartyUpdate = {};
      if (updates.name !== undefined) dbUpdates.name = updates.name;
      if (updates.kind !== undefined) dbUpdates.kind = updates.kind;
      if (updates.document !== undefined) dbUpdates.document = updates.document ?? null;
      if (updates.email !== undefined) dbUpdates.email = updates.email ?? null;
      if (updates.phone !== undefined) dbUpdates.phone = updates.phone ?? null;
      if (updates.notes !== undefined) dbUpdates.notes = updates.notes ?? null;
      const { error } = await supabase.from('counterparties').update(dbUpdates).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['counterparties', clientId] });
      toast.success('Contraparte atualizada!');
    },
    onError: (e: Error) => toast.error('Erro ao atualizar contraparte: ' + e.message),
  });

  const deleteCounterpartyMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('counterparties')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['counterparties', clientId] });
      toast.success('Contraparte excluída!');
    },
    onError: (e: Error) => toast.error('Erro ao excluir contraparte: ' + e.message),
  });

  // Transaction mutations
  const addTransactionMutation = useMutation({
    mutationFn: async (transaction: Omit<FinanceTransaction, 'id' | 'createdAt' | 'updatedAt'>) => {
      if (!clientId) throw new Error('Cliente não selecionado');

      const hasSplit = !!(transaction.categorySplits && transaction.categorySplits.length > 0);
      const hasCcSplit = !!(transaction.costCenterSplits && transaction.costCenterSplits.length > 0);

      const baseRow = {
        client_id: clientId,
        type: transaction.type,
        description: transaction.description,
        amount: transaction.amount,
        // Quando há split, category_id fica NULL (categoria efetiva vem do split).
        category_id: hasSplit ? null : transaction.categoryId || null,
        account_id: transaction.accountId,
        destination_account_id: transaction.destinationAccountId ?? null,
        // Idem para centro de custo: com split, cost_center_id fica NULL.
        cost_center_id: hasCcSplit ? null : (transaction.costCenterId ?? null),
        counterparty_id: transaction.counterpartyId ?? null,
        interest: transaction.interest ?? 0,
        fine: transaction.fine ?? 0,
        external_id: transaction.externalId ?? null,
        status: transaction.status,
        notes: transaction.notes ?? null,
        approval_status: transaction.approvalStatus ?? 'not_required',
      };

      const splits = transaction.categorySplits ?? [];
      const ccSplits = transaction.costCenterSplits ?? [];

      const insertSplitsFor = async (txIds: string[]) => {
        if (splits.length === 0) return;
        const rows = txIds.flatMap((txId) =>
          splits.map((s, idx) => ({
            transaction_id: txId,
            category_id: s.categoryId,
            amount: s.amount,
            order_index: s.orderIndex ?? idx,
          })),
        );
        const { error } = await supabase.from('transaction_category_splits').insert(rows);
        if (error) throw error;
      };

      const insertCcSplitsFor = async (txIds: string[]) => {
        if (ccSplits.length === 0) return;
        const rows = txIds.flatMap((txId) =>
          ccSplits.map((s, idx) => ({
            transaction_id: txId,
            cost_center_id: s.costCenterId,
            amount: s.amount,
            order_index: s.orderIndex ?? idx,
          })),
        );
        const { error } = await supabase.from('transaction_cost_center_splits').insert(rows);
        if (error) throw error;
      };

      if (
        transaction.recurrence !== 'none' &&
        transaction.numberOfRepetitions &&
        transaction.numberOfRepetitions > 1
      ) {
        const toInsert = [];
        let currentDate = transaction.dueDate;
        const recurrenceGroupId = crypto.randomUUID();

        for (let i = 0; i < transaction.numberOfRepetitions; i++) {
          toInsert.push({
            ...baseRow,
            date: format(currentDate, 'yyyy-MM-dd'),
            is_recurring: true,
            recurrence_type: transaction.recurrence,
            recurrence_group_id: recurrenceGroupId,
            number_of_repetitions: transaction.numberOfRepetitions,
          });
          currentDate = getNextRecurrenceDate(currentDate, transaction.recurrence);
        }

        const { data, error } = await supabase.from('transactions').insert(toInsert).select('id');
        if (error) throw error;
        // Recorrência herda o mesmo split em todas as parcelas
        const insertedIds = (data ?? []).map((r) => r.id as string);
        await insertSplitsFor(insertedIds);
        await insertCcSplitsFor(insertedIds);
      } else {
        const { data, error } = await supabase
          .from('transactions')
          .insert({
            ...baseRow,
            date: format(transaction.dueDate, 'yyyy-MM-dd'),
            is_recurring: false,
            recurrence_type: null,
            recurrence_group_id: null,
          })
          .select('id')
          .single();
        if (error) throw error;
        await insertSplitsFor([data.id as string]);
        await insertCcSplitsFor([data.id as string]);
      }
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ['transactions', clientId] });
      queryClient.invalidateQueries({ queryKey: ['accounts', clientId] });
      const msg =
        vars.numberOfRepetitions && vars.numberOfRepetitions > 1
          ? `${vars.numberOfRepetitions} transações criadas!`
          : 'Transação criada!';
      toast.success(msg);
    },
    onError: (e: Error) => toast.error('Erro ao criar transação: ' + e.message),
  });

  const updateTransactionMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<FinanceTransaction> }) => {
      const dbUpdates: TransactionUpdate = {};
      if (updates.type !== undefined) dbUpdates.type = updates.type;
      if (updates.description !== undefined) dbUpdates.description = updates.description;
      if (updates.amount !== undefined) dbUpdates.amount = updates.amount;
      if (updates.dueDate !== undefined) dbUpdates.date = format(updates.dueDate, 'yyyy-MM-dd');
      if ('paymentDate' in updates)
        dbUpdates.payment_date = updates.paymentDate
          ? format(updates.paymentDate, 'yyyy-MM-dd')
          : null;
      // Quando há split no payload, category_id da transação fica NULL.
      const hasSplitInPayload = !!(updates.categorySplits && updates.categorySplits.length > 0);
      const clearsSplit =
        'categorySplits' in updates &&
        (!updates.categorySplits || updates.categorySplits.length === 0);
      if (hasSplitInPayload) {
        dbUpdates.category_id = null;
      } else if (updates.categoryId !== undefined) {
        dbUpdates.category_id = updates.categoryId || null;
      }
      if (updates.accountId !== undefined) dbUpdates.account_id = updates.accountId;
      // Mesmo padrão para centro de custo: split no payload zera o escalar.
      const hasCcSplitInPayload = !!(updates.costCenterSplits && updates.costCenterSplits.length > 0);
      const clearsCcSplit =
        'costCenterSplits' in updates &&
        (!updates.costCenterSplits || updates.costCenterSplits.length === 0);
      if (hasCcSplitInPayload) {
        dbUpdates.cost_center_id = null;
      } else if ('costCenterId' in updates) {
        dbUpdates.cost_center_id = updates.costCenterId ?? null;
      }
      if ('counterpartyId' in updates) dbUpdates.counterparty_id = updates.counterpartyId ?? null;
      if ('destinationAccountId' in updates)
        dbUpdates.destination_account_id = updates.destinationAccountId ?? null;
      if (updates.status !== undefined) dbUpdates.status = updates.status;
      if ('notes' in updates) dbUpdates.notes = updates.notes ?? null;
      if ('interest' in updates) dbUpdates.interest = updates.interest ?? 0;
      if ('fine' in updates) dbUpdates.fine = updates.fine ?? 0;

      // Splits: ordem importante para o trigger DEFERRABLE validar a soma só no commit.
      // 1) Atualizar a transação (amount + category_id).
      // 2) DELETE splits antigos.
      // 3) INSERT splits novos (se houver).
      const { error: updErr } = await supabase.from('transactions').update(dbUpdates).eq('id', id);
      if (updErr) throw updErr;

      if (hasSplitInPayload || clearsSplit) {
        const { error: delErr } = await supabase
          .from('transaction_category_splits')
          .delete()
          .eq('transaction_id', id);
        if (delErr) throw delErr;
      }

      if (hasSplitInPayload && updates.categorySplits) {
        const rows = updates.categorySplits.map((s, idx) => ({
          transaction_id: id,
          category_id: s.categoryId,
          amount: s.amount,
          order_index: s.orderIndex ?? idx,
        }));
        const { error: insErr } = await supabase.from('transaction_category_splits').insert(rows);
        if (insErr) throw insErr;
      }

      if (hasCcSplitInPayload || clearsCcSplit) {
        const { error: delErr } = await supabase
          .from('transaction_cost_center_splits')
          .delete()
          .eq('transaction_id', id);
        if (delErr) throw delErr;
      }

      if (hasCcSplitInPayload && updates.costCenterSplits) {
        const rows = updates.costCenterSplits.map((s, idx) => ({
          transaction_id: id,
          cost_center_id: s.costCenterId,
          amount: s.amount,
          order_index: s.orderIndex ?? idx,
        }));
        const { error: insErr } = await supabase
          .from('transaction_cost_center_splits')
          .insert(rows);
        if (insErr) throw insErr;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transactions', clientId] });
      queryClient.invalidateQueries({ queryKey: ['accounts', clientId] });
      toast.success('Transação atualizada!');
    },
    onError: (e: Error) => toast.error('Erro ao atualizar transação: ' + e.message),
  });

  const updateRecurringTransactionMutation = useMutation({
    mutationFn: async ({
      id,
      updates,
      scope,
      transaction,
    }: {
      id: string;
      updates: Partial<FinanceTransaction>;
      scope: RecurrenceActionScope;
      transaction: FinanceTransaction;
    }) => {
      const buildDbUpdates = (): TransactionUpdate => {
        const u: TransactionUpdate = {};
        if (updates.type !== undefined) u.type = updates.type;
        if (updates.description !== undefined) u.description = updates.description;
        if (updates.amount !== undefined) u.amount = updates.amount;
        if (updates.categoryId !== undefined) u.category_id = updates.categoryId || null;
        if (updates.accountId !== undefined) u.account_id = updates.accountId;
        if ('costCenterId' in updates) u.cost_center_id = updates.costCenterId ?? null;
        if ('notes' in updates) u.notes = updates.notes ?? null;
        if ('interest' in updates) u.interest = updates.interest ?? 0;
        if ('fine' in updates) u.fine = updates.fine ?? 0;
        return u;
      };

      const groupId = transaction.recurrenceGroupId!;
      const transactionDate = format(transaction.dueDate, 'yyyy-MM-dd');

      if (scope === 'single') {
        const singleUpdates: TransactionUpdate = { ...buildDbUpdates() };
        if (updates.dueDate !== undefined)
          singleUpdates.date = format(updates.dueDate, 'yyyy-MM-dd');
        if (updates.status !== undefined) singleUpdates.status = updates.status;
        if ('paymentDate' in updates)
          singleUpdates.payment_date = updates.paymentDate
            ? format(updates.paymentDate, 'yyyy-MM-dd')
            : null;
        if (Object.keys(singleUpdates).length === 0)
          singleUpdates.updated_at = new Date().toISOString();
        const { error } = await supabase.from('transactions').update(singleUpdates).eq('id', id);
        if (error) throw error;
        return;
      }

      let transactionsQuery = supabase
        .from('transactions')
        .select('id, date, recurrence_type')
        .eq('recurrence_group_id', groupId)
        .order('date', { ascending: true });

      if (scope === 'future') transactionsQuery = transactionsQuery.gte('date', transactionDate);

      const { data: groupTransactions, error: fetchError } = await transactionsQuery;
      if (fetchError) throw fetchError;
      if (!groupTransactions || groupTransactions.length === 0) return;

      const dbUpdates = buildDbUpdates();
      const hasDateChange = updates.dueDate !== undefined;
      const hasOtherUpdates = Object.keys(dbUpdates).length > 0;

      if (hasDateChange && updates.dueDate) {
        const newStartDate = updates.dueDate;
        const recurrenceType =
          (groupTransactions[0]?.['recurrence_type'] as string | null) ?? 'monthly';

        for (let i = 0; i < groupTransactions.length; i++) {
          const t = groupTransactions[i]!;
          let newDate: Date;
          switch (recurrenceType) {
            case 'daily':
              newDate = addDays(newStartDate, i);
              break;
            case 'weekly':
              newDate = addWeeks(newStartDate, i);
              break;
            case 'biweekly':
              newDate = addWeeks(newStartDate, i * 2);
              break;
            case 'yearly':
              newDate = addYears(newStartDate, i);
              break;
            default:
              newDate = addMonths(newStartDate, i);
          }
          const updatePayload: TransactionUpdate = {
            ...dbUpdates,
            date: format(newDate, 'yyyy-MM-dd'),
          };
          const { error } = await supabase
            .from('transactions')
            .update(updatePayload)
            .eq('id', t['id'] as string);
          if (error) throw error;
        }
      } else if (hasOtherUpdates) {
        let query = supabase
          .from('transactions')
          .update(dbUpdates)
          .eq('recurrence_group_id', groupId);
        if (scope === 'future') query = query.gte('date', transactionDate);
        const { error } = await query;
        if (error) throw error;
      }
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ['transactions', clientId], refetchType: 'all' });
      queryClient.invalidateQueries({ queryKey: ['accounts', clientId], refetchType: 'all' });
      const msg =
        vars.scope === 'single'
          ? 'Transação atualizada!'
          : vars.scope === 'future'
            ? 'Esta e futuras atualizadas!'
            : 'Toda a série atualizada!';
      toast.success(msg);
    },
    onError: (e: Error) => toast.error('Erro ao atualizar transações: ' + e.message),
  });

  const deleteTransactionMutation = useMutation({
    mutationFn: async (id: string) => {
      // RPC soft_delete_transaction captura erros de trigger/constraint/RLS
      // como jsonb estruturado em vez de mascarar via PostgREST.
      const { data, error } = await supabase.rpc('soft_delete_transaction', { p_id: id });
      if (error) {
        console.error('[deleteTransaction] RPC falhou', { id, error });
        throw new Error(error.message);
      }
      const result = data as {
        success: boolean;
        error?: string;
        message?: string;
        sqlstate?: string;
        detail?: string;
        hint?: string;
        context?: string;
      };
      if (!result.success) {
        console.error('[deleteTransaction] falhou', { id, result });
        const parts = [
          result.message,
          result.detail,
          result.hint,
          result.sqlstate ? `[${result.sqlstate}]` : null,
        ].filter(Boolean);
        throw new Error(parts.join(' · ') || 'Erro desconhecido');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transactions', clientId] });
      queryClient.invalidateQueries({ queryKey: ['accounts', clientId] });
      toast.success('Transação excluída!');
    },
    onError: (e: Error) => toast.error('Erro ao excluir transação: ' + e.message),
  });

  const deleteRecurringTransactionMutation = useMutation({
    mutationFn: async ({
      id,
      scope,
      transaction,
    }: {
      id: string;
      scope: RecurrenceActionScope;
      transaction: FinanceTransaction;
    }) => {
      const groupId = transaction.recurrenceGroupId!;
      const transactionDate = format(transaction.dueDate, 'yyyy-MM-dd');

      const nowIso = new Date().toISOString();
      switch (scope) {
        case 'single': {
          const { error } = await supabase
            .from('transactions')
            .update({ deleted_at: nowIso })
            .eq('id', id);
          if (error) throw error;
          break;
        }
        case 'future': {
          const { error } = await supabase
            .from('transactions')
            .update({ deleted_at: nowIso })
            .eq('recurrence_group_id', groupId)
            .gte('date', transactionDate);
          if (error) throw error;
          break;
        }
        case 'all': {
          const { error } = await supabase
            .from('transactions')
            .update({ deleted_at: nowIso })
            .eq('recurrence_group_id', groupId);
          if (error) throw error;
          break;
        }
      }
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ['transactions', clientId] });
      queryClient.invalidateQueries({ queryKey: ['accounts', clientId] });
      const msg =
        vars.scope === 'single'
          ? 'Transação excluída!'
          : vars.scope === 'future'
            ? 'Esta e futuras excluídas!'
            : 'Toda a série excluída!';
      toast.success(msg);
    },
    onError: (e: Error) => toast.error('Erro ao excluir transações: ' + e.message),
  });

  // Callback wrappers
  const addTransaction = useCallback(
    (t: Omit<FinanceTransaction, 'id' | 'createdAt' | 'updatedAt'>) => {
      addTransactionMutation.mutate(t);
    },
    [addTransactionMutation],
  );
  const updateTransaction = useCallback(
    (id: string, t: Partial<FinanceTransaction>) => {
      updateTransactionMutation.mutate({ id, updates: t });
    },
    [updateTransactionMutation],
  );
  const deleteTransaction = useCallback(
    (id: string) => {
      deleteTransactionMutation.mutate(id);
    },
    [deleteTransactionMutation],
  );
  const deleteRecurringTransaction = useCallback(
    (id: string, scope: RecurrenceActionScope, t: FinanceTransaction) => {
      deleteRecurringTransactionMutation.mutate({ id, scope, transaction: t });
    },
    [deleteRecurringTransactionMutation],
  );
  const updateRecurringTransaction = useCallback(
    (
      id: string,
      updates: Partial<FinanceTransaction>,
      scope: RecurrenceActionScope,
      t: FinanceTransaction,
    ) => {
      updateRecurringTransactionMutation.mutate({ id, updates, scope, transaction: t });
    },
    [updateRecurringTransactionMutation],
  );
  const markAsCompleted = useCallback(
    (id: string, opts?: { paymentDate?: Date; interest?: number; fine?: number }) => {
      const updates: Partial<FinanceTransaction> = {
        status: 'completed',
        paymentDate: opts?.paymentDate ?? new Date(),
      };
      if (opts?.interest !== undefined) updates.interest = opts.interest;
      if (opts?.fine !== undefined) updates.fine = opts.fine;
      updateTransactionMutation.mutate({ id, updates });
    },
    [updateTransactionMutation],
  );

  const addAccount = useCallback(
    (a: Omit<FinanceAccount, 'id' | 'createdAt'>) => {
      addAccountMutation.mutate(a);
    },
    [addAccountMutation],
  );
  const updateAccount = useCallback(
    (id: string, a: Partial<FinanceAccount>) => {
      updateAccountMutation.mutate({ id, updates: a });
    },
    [updateAccountMutation],
  );
  const deleteAccount = useCallback(
    (id: string) => {
      deleteAccountMutation.mutate(id);
    },
    [deleteAccountMutation],
  );

  const addCategory = useCallback(
    (c: Omit<FinanceCategory, 'id'>) => {
      addCategoryMutation.mutate(c);
    },
    [addCategoryMutation],
  );
  const updateCategory = useCallback(
    (id: string, c: Partial<FinanceCategory>) => {
      updateCategoryMutation.mutate({ id, updates: c });
    },
    [updateCategoryMutation],
  );
  const deleteCategory = useCallback(
    (id: string) => {
      deleteCategoryMutation.mutate(id);
    },
    [deleteCategoryMutation],
  );

  const addCostCenter = useCallback(
    (cc: Omit<FinanceCostCenter, 'id' | 'createdAt'>) => {
      addCostCenterMutation.mutate(cc);
    },
    [addCostCenterMutation],
  );
  const updateCostCenter = useCallback(
    (id: string, cc: Partial<FinanceCostCenter>) => {
      updateCostCenterMutation.mutate({ id, updates: cc });
    },
    [updateCostCenterMutation],
  );
  const deleteCostCenter = useCallback(
    (id: string) => {
      deleteCostCenterMutation.mutate(id);
    },
    [deleteCostCenterMutation],
  );

  const addCounterparty = useCallback(
    (cp: Omit<FinanceCounterparty, 'id' | 'createdAt' | 'updatedAt'>) =>
      addCounterpartyMutation.mutateAsync(cp),
    [addCounterpartyMutation],
  );
  const updateCounterparty = useCallback(
    (id: string, cp: Partial<FinanceCounterparty>) => {
      updateCounterpartyMutation.mutate({ id, updates: cp });
    },
    [updateCounterpartyMutation],
  );
  const deleteCounterparty = useCallback(
    (id: string) => {
      deleteCounterpartyMutation.mutate(id);
    },
    [deleteCounterpartyMutation],
  );

  const getCategoryById = useCallback(
    (id: string) => categories.find((c) => c.id === id),
    [categories],
  );
  const getAccountById = useCallback((id: string) => accounts.find((a) => a.id === id), [accounts]);
  const getCostCenterById = useCallback(
    (id: string) => costCenters.find((cc) => cc.id === id),
    [costCenters],
  );
  const getCategoryByName = useCallback(
    (name: string, type: TransactionType) =>
      categories.find((c) => normalizeName(c.name) === normalizeName(name) && c.type === type),
    [categories],
  );
  const getCostCenterByName = useCallback(
    (name: string) =>
      costCenters.find((cc) => cc.isActive && normalizeName(cc.name) === normalizeName(name)),
    [costCenters],
  );

  const importBatch = useCallback(
    async (input: ImportBatchInput): Promise<ImportBatchResult> => {
      if (!clientId) throw new Error('Cliente não selecionado');
      const errors: { line?: number; reason: string }[] = [];
      const catNameToId = new Map<string, string>();
      const ccNameToId = new Map<string, string>();
      let categoriesCreated = 0;
      let costCentersCreated = 0;

      // Dedup defensivo: se já existe categoria com mesmo nome+tipo, reusar em vez de tentar criar
      const filteredCatsToCreate = input.categoriesToCreate.filter((c) => {
        const existing = categories.find(
          (x) => normalizeName(x.name) === normalizeName(c.name) && x.type === c.type,
        );
        if (existing) {
          catNameToId.set(`${normalizeName(c.name)}::${c.type}`, existing.id);
          return false;
        }
        return true;
      });

      // Pré-popula com TODOS os CCs já existentes (não só os que vieram em costCentersToCreate)
      // — assim transactions com costCenterName que não passaram pelo MappingStep create
      // ainda resolvem se o CC já existir.
      for (const cc of costCenters) {
        if (cc.isActive) ccNameToId.set(normalizeName(cc.name), cc.id);
      }

      // Dedup defensivo CC: se já existe ativo com mesmo nome, reusar
      const filteredCcsToCreate = input.costCentersToCreate.filter((cc) => {
        const existing = costCenters.find(
          (x) => x.isActive && normalizeName(x.name) === normalizeName(cc.name),
        );
        if (existing) {
          ccNameToId.set(normalizeName(cc.name), existing.id);
          return false;
        }
        return true;
      });

      if (filteredCatsToCreate.length > 0) {
        const { data, error } = await supabase
          .from('categories')
          .insert(
            filteredCatsToCreate.map((c) => ({
              client_id: clientId,
              name: c.name,
              type: c.type,
              color: c.color,
              icon: 'Circle',
            })),
          )
          .select();
        if (error) {
          errors.push({ reason: `Erro ao criar categorias: ${error.message}` });
        } else if (data) {
          categoriesCreated = data.length;
          data.forEach((row) => {
            const key = `${normalizeName(String(row['name']))}::${row['type']}`;
            catNameToId.set(key, row['id'] as string);
          });
        }
      }

      if (filteredCcsToCreate.length > 0) {
        const { data, error } = await supabase
          .from('cost_centers')
          .insert(
            filteredCcsToCreate.map((cc) => ({
              client_id: clientId,
              name: cc.name,
              color: cc.color,
              is_active: true,
            })),
          )
          .select();
        if (error) {
          errors.push({ reason: `Erro ao criar centros de custo: ${error.message}` });
        } else if (data) {
          costCentersCreated = data.length;
          data.forEach((row) => {
            ccNameToId.set(normalizeName(String(row['name'])), row['id'] as string);
          });
        }
      }

      const transactionsPayload = input.transactions.map((t) => {
        let categoryId: string | null = t.existingCategoryId ?? null;
        if (!categoryId && t.categoryName) {
          const key = `${normalizeName(t.categoryName)}::${t.type}`;
          categoryId = catNameToId.get(key) ?? null;
        }
        let costCenterId: string | null = t.existingCostCenterId ?? null;
        if (!costCenterId && t.costCenterName) {
          costCenterId = ccNameToId.get(normalizeName(t.costCenterName)) ?? null;
          if (!costCenterId && import.meta.env.DEV) {
            console.warn(
              `[importBatch] Centro de custo "${t.costCenterName}" não resolvido para uma transação. ` +
                `Verifique se o nome bate com algum CC ativo do cliente.`,
            );
          }
        }
        return {
          client_id: clientId,
          type: t.type,
          description: t.description,
          amount: t.amount,
          date: format(t.dueDate, 'yyyy-MM-dd'),
          payment_date: t.paymentDate ? format(t.paymentDate, 'yyyy-MM-dd') : null,
          category_id: categoryId,
          account_id: t.accountId,
          destination_account_id: t.destinationAccountId ?? null,
          cost_center_id: costCenterId,
          status: t.status,
          notes: t.notes ?? null,
          external_id: t.externalId ?? null,
          is_recurring: false,
          recurrence_type: null,
          recurrence_group_id: null,
        };
      });

      // Filtra duplicatas por external_id: verifica quais já existem no banco
      // pra mesma (client, account, external_id). Sob o unique index parcial
      // garante que nem mesmo erros de race condition criem duplicatas.
      let transactionsSkippedDuplicate = 0;
      let payloadToInsert = transactionsPayload;
      const rowsWithExternalId = transactionsPayload.filter((r) => r.external_id);
      if (rowsWithExternalId.length > 0) {
        const externalIds = rowsWithExternalId.map((r) => r.external_id as string);
        const { data: existing } = await supabase
          .from('transactions')
          .select('external_id, account_id')
          .eq('client_id', clientId)
          .is('deleted_at', null)
          .in('external_id', externalIds);
        if (existing && existing.length > 0) {
          const existingKeys = new Set(existing.map((e) => `${e.account_id}::${e.external_id}`));
          payloadToInsert = transactionsPayload.filter((r) => {
            if (!r.external_id) return true;
            const key = `${r.account_id}::${r.external_id}`;
            if (existingKeys.has(key)) {
              transactionsSkippedDuplicate += 1;
              return false;
            }
            return true;
          });
        }
      }

      let transactionsCreated = 0;
      if (payloadToInsert.length > 0) {
        const CHUNK_SIZE = 100;
        for (let i = 0; i < payloadToInsert.length; i += CHUNK_SIZE) {
          const chunk = payloadToInsert.slice(i, i + CHUNK_SIZE);
          const { error, count } = await supabase
            .from('transactions')
            .insert(chunk, { count: 'exact' });
          if (error) {
            errors.push({
              reason: `Erro ao inserir transações (lote ${Math.floor(i / CHUNK_SIZE) + 1}): ${error.message}`,
            });
          } else {
            transactionsCreated += count ?? chunk.length;
          }
        }
      }

      await queryClient.invalidateQueries({ queryKey: ['categories', clientId] });
      await queryClient.invalidateQueries({ queryKey: ['cost_centers', clientId] });
      await queryClient.invalidateQueries({ queryKey: ['transactions', clientId] });
      await queryClient.invalidateQueries({ queryKey: ['accounts', clientId] });

      return {
        categoriesCreated,
        costCentersCreated,
        transactionsCreated,
        transactionsSkippedDuplicate,
        errors,
      };
    },
    [clientId, queryClient, categories, costCenters],
  );

  const invalidateAfterBulk = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['transactions', clientId] });
    await queryClient.invalidateQueries({ queryKey: ['accounts', clientId] });
  }, [queryClient, clientId]);

  const bulkMarkCompleted = useCallback(
    async (ids: string[]): Promise<number> => {
      if (ids.length === 0) return 0;
      const { data, error } = await supabase.rpc('bulk_mark_transactions_completed', {
        p_ids: ids,
      });
      if (error) {
        toast.error(`Erro ao marcar como pago: ${error.message}`);
        throw error;
      }
      const count = typeof data === 'number' ? data : ids.length;
      await invalidateAfterBulk();
      toast.success(`${count} transação(ões) marcada(s) como paga(s)`);
      return count;
    },
    [invalidateAfterBulk],
  );

  /**
   * Reconcilia transações pendentes com lançamentos do extrato OFX.
   * Para cada match: marca status='completed', preenche payment_date e ajusta amount
   * (se diverge do lançamento — pode ter juros/multa). Trigger update_account_balance
   * recalcula o saldo da conta automaticamente.
   */
  const reconcileMatches = useCallback(
    async (matches: ReconcileMatchInput[]): Promise<ReconcileMatchesResult> => {
      const errors: { transactionId?: string; reason: string }[] = [];
      let reconciled = 0;

      for (const m of matches) {
        const { error } = await supabase
          .from('transactions')
          .update({
            status: 'completed',
            payment_date: m.paymentDate,
            amount: m.paidAmount,
          })
          .eq('id', m.transactionId)
          .eq('status', 'pending');
        if (error) {
          errors.push({ transactionId: m.transactionId, reason: error.message });
        } else {
          reconciled += 1;
        }
      }

      if (reconciled > 0) {
        await queryClient.invalidateQueries({ queryKey: ['transactions', clientId] });
        await queryClient.invalidateQueries({ queryKey: ['accounts', clientId] });
      }
      return { reconciled, errors };
    },
    [queryClient, clientId],
  );

  const bulkMarkPending = useCallback(
    async (ids: string[]): Promise<number> => {
      if (ids.length === 0) return 0;
      const { error, count } = await supabase
        .from('transactions')
        .update({ status: 'pending', payment_date: null }, { count: 'exact' })
        .in('id', ids);
      if (error) {
        toast.error(`Erro ao marcar como pendente: ${error.message}`);
        throw error;
      }
      const affected = count ?? ids.length;
      await invalidateAfterBulk();
      toast.success(`${affected} transação(ões) marcada(s) como pendente(s)`);
      return affected;
    },
    [invalidateAfterBulk],
  );

  const bulkUpdateCostCenter = useCallback(
    async (ids: string[], costCenterId: string | null): Promise<number> => {
      if (ids.length === 0) return 0;
      // Definir CC escalar sobrescreve splits existentes; sem este DELETE,
      // escalar + splits coexistiriam e o split (silenciosamente) venceria na leitura.
      const { error: delErr } = await supabase
        .from('transaction_cost_center_splits')
        .delete()
        .in('transaction_id', ids);
      if (delErr) {
        toast.error(`Erro ao atualizar centro de custo: ${delErr.message}`);
        throw delErr;
      }
      const { error, count } = await supabase
        .from('transactions')
        .update({ cost_center_id: costCenterId }, { count: 'exact' })
        .in('id', ids);
      if (error) {
        toast.error(`Erro ao atualizar centro de custo: ${error.message}`);
        throw error;
      }
      const affected = count ?? ids.length;
      await invalidateAfterBulk();
      toast.success(`${affected} transação(ões) atualizada(s)`);
      return affected;
    },
    [invalidateAfterBulk],
  );

  const bulkUpdateCounterparty = useCallback(
    async (ids: string[], counterpartyId: string | null): Promise<number> => {
      if (ids.length === 0) return 0;
      const { error, count } = await supabase
        .from('transactions')
        .update({ counterparty_id: counterpartyId }, { count: 'exact' })
        .in('id', ids);
      if (error) {
        toast.error(`Erro ao atualizar contraparte: ${error.message}`);
        throw error;
      }
      const affected = count ?? ids.length;
      await invalidateAfterBulk();
      toast.success(`${affected} transação(ões) atualizada(s)`);
      return affected;
    },
    [invalidateAfterBulk],
  );

  const closeCreditCardInvoice = useCallback(
    async (input: CloseInvoiceInput): Promise<CloseInvoiceResult> => {
      const { data, error } = await supabase.rpc('close_credit_card_invoice', {
        p_card_account_id: input.cardAccountId,
        p_period_start: input.periodStart,
        p_period_end: input.periodEnd,
        p_payment_account_id: input.paymentAccountId,
        p_payment_date: input.paymentDate,
      });
      if (error) {
        toast.error(`Erro ao fechar fatura: ${error.message}`);
        throw error;
      }
      const result = data as { closed_count: number; total_amount: number; transfer_id: string };
      await invalidateAfterBulk();
      await queryClient.invalidateQueries({ queryKey: ['accounts', clientId] });
      toast.success(
        `Fatura fechada: ${result.closed_count} transação(ões), total ${new Intl.NumberFormat(
          'pt-BR',
          { style: 'currency', currency: 'BRL' },
        ).format(Number(result.total_amount))}`,
      );
      return {
        closedCount: result.closed_count,
        totalAmount: Number(result.total_amount),
        transferId: result.transfer_id,
      };
    },
    [invalidateAfterBulk, queryClient, clientId],
  );

  const approveTransactions = useCallback(
    async (ids: string[], approvedByUserId: string): Promise<number> => {
      if (ids.length === 0) return 0;
      const { error, count } = await supabase
        .from('transactions')
        .update(
          {
            approval_status: 'approved',
            approved_at: new Date().toISOString(),
            approved_by_user_id: approvedByUserId,
            rejection_reason: null,
          },
          { count: 'exact' },
        )
        .in('id', ids)
        .eq('approval_status', 'awaiting');
      if (error) {
        toast.error(`Erro ao aprovar: ${error.message}`);
        throw error;
      }
      const affected = count ?? ids.length;
      await invalidateAfterBulk();
      toast.success(`${affected} pagamento(s) aprovado(s)`);
      return affected;
    },
    [invalidateAfterBulk],
  );

  const rejectTransactions = useCallback(
    async (ids: string[], reason: string): Promise<number> => {
      if (ids.length === 0) return 0;
      const { error, count } = await supabase
        .from('transactions')
        .update(
          {
            approval_status: 'rejected',
            rejection_reason: reason.trim() || null,
          },
          { count: 'exact' },
        )
        .in('id', ids)
        .eq('approval_status', 'awaiting');
      if (error) {
        toast.error(`Erro ao rejeitar: ${error.message}`);
        throw error;
      }
      const affected = count ?? ids.length;
      await invalidateAfterBulk();
      toast.success(`${affected} pagamento(s) rejeitado(s)`);
      return affected;
    },
    [invalidateAfterBulk],
  );

  const bulkDelete = useCallback(
    async (ids: string[]): Promise<number> => {
      if (ids.length === 0) return 0;
      // Usa RPC bulk_soft_delete_transactions: captura erros de trigger/RLS
      // como jsonb estruturado, idem soft_delete_transaction (single).
      const { data, error } = await supabase.rpc('bulk_soft_delete_transactions', {
        p_ids: ids,
      });
      if (error) {
        console.error('[bulkDelete] RPC falhou', { ids, error });
        toast.error(`Erro ao excluir: ${error.message}`);
        throw error;
      }
      const result = data as {
        success: boolean;
        rows_affected?: number;
        error?: string;
        message?: string;
        sqlstate?: string;
        detail?: string;
        hint?: string;
      };
      if (!result.success) {
        console.error('[bulkDelete] falhou', { ids, result });
        const parts = [
          result.message,
          result.detail,
          result.hint,
          result.sqlstate ? `[${result.sqlstate}]` : null,
        ].filter(Boolean);
        const msg = parts.join(' · ') || 'Erro desconhecido';
        toast.error(`Erro ao excluir: ${msg}`);
        throw new Error(msg);
      }
      const affected = result.rows_affected ?? ids.length;
      await invalidateAfterBulk();
      toast.success(`${affected} transação(ões) excluída(s)`);
      return affected;
    },
    [invalidateAfterBulk],
  );

  const getFilteredTransactions = useCallback((): FinanceTransaction[] => {
    return transactions.filter((t) => {
      if (t.dueDate.getFullYear() !== filters.year) return false;
      if (filters.periodMode === 'daily' && filters.month !== undefined) {
        if (t.dueDate.getMonth() !== filters.month) return false;
      }
      if (filters.accountId !== 'all' && t.accountId !== filters.accountId) return false;
      if (filters.costCenterId && filters.costCenterId !== 'all') {
        const ccMatch =
          t.costCenterId === filters.costCenterId ||
          (t.costCenterSplits?.some((s) => s.costCenterId === filters.costCenterId) ?? false);
        if (!ccMatch) return false;
      }
      if (filters.viewMode === 'realized' && t.status !== 'completed') return false;
      if (filters.viewMode === 'predicted' && t.status === 'completed') return false;
      return true;
    });
  }, [transactions, filters]);

  const getChartData = useCallback((): ChartDataPoint[] => {
    const filtered = getFilteredTransactions();
    const initialBalance = accounts.reduce((sum, a) => sum + a.initialBalance, 0);

    if (filters.periodMode === 'monthly') {
      const start = startOfYear(new Date(filters.year, 0, 1));
      const end = endOfYear(new Date(filters.year, 0, 1));
      const months = eachMonthOfInterval({ start, end });
      let runPredicted = initialBalance;
      let runRealized = initialBalance;

      return months.map((month) => {
        const mt = filtered.filter((t) => isSameMonth(t.dueDate, month));
        const incomePredicted = mt
          .filter((t) => t.type === 'income' && t.status !== 'completed')
          .reduce((s, t) => s + t.amount, 0);
        const incomeRealized = mt
          .filter((t) => t.type === 'income' && t.status === 'completed')
          .reduce((s, t) => s + t.amount, 0);
        const expensePredicted = mt
          .filter((t) => t.type === 'expense' && t.status !== 'completed')
          .reduce((s, t) => s + t.amount, 0);
        const expenseRealized = mt
          .filter((t) => t.type === 'expense' && t.status === 'completed')
          .reduce((s, t) => s + t.amount, 0);
        runPredicted += incomePredicted + incomeRealized - (expensePredicted + expenseRealized);
        runRealized += incomeRealized - expenseRealized;
        return {
          period: format(month, 'MMM', { locale: ptBR }),
          incomePredicted,
          incomeRealized,
          expensePredicted,
          expenseRealized,
          balancePredicted: runPredicted,
          balanceRealized: runRealized,
        };
      });
    } else {
      const selectedMonth = filters.month ?? new Date().getMonth();
      const start = startOfMonth(new Date(filters.year, selectedMonth, 1));
      const end = endOfMonth(new Date(filters.year, selectedMonth, 1));
      const days = eachDayOfInterval({ start, end });
      let runPredicted = initialBalance;
      let runRealized = initialBalance;

      return days.map((day) => {
        const dt = filtered.filter((t) => isSameDay(t.dueDate, day));
        const incomePredicted = dt
          .filter((t) => t.type === 'income' && t.status !== 'completed')
          .reduce((s, t) => s + t.amount, 0);
        const incomeRealized = dt
          .filter((t) => t.type === 'income' && t.status === 'completed')
          .reduce((s, t) => s + t.amount, 0);
        const expensePredicted = dt
          .filter((t) => t.type === 'expense' && t.status !== 'completed')
          .reduce((s, t) => s + t.amount, 0);
        const expenseRealized = dt
          .filter((t) => t.type === 'expense' && t.status === 'completed')
          .reduce((s, t) => s + t.amount, 0);
        runPredicted += incomePredicted + incomeRealized - (expensePredicted + expenseRealized);
        runRealized += incomeRealized - expenseRealized;
        return {
          period: format(day, 'dd'),
          incomePredicted,
          incomeRealized,
          expensePredicted,
          expenseRealized,
          balancePredicted: runPredicted,
          balanceRealized: runRealized,
        };
      });
    }
  }, [getFilteredTransactions, filters, accounts]);

  const getDashboardSummary = useCallback((): DashboardSummary => {
    const filtered = getFilteredTransactions();
    const totalIncomePredicted = filtered
      .filter((t) => t.type === 'income' && t.status !== 'completed')
      .reduce((s, t) => s + t.amount, 0);
    const totalIncomeRealized = filtered
      .filter((t) => t.type === 'income' && t.status === 'completed')
      .reduce((s, t) => s + t.amount, 0);
    const totalExpensePredicted = filtered
      .filter((t) => t.type === 'expense' && t.status !== 'completed')
      .reduce((s, t) => s + t.amount, 0);
    const totalExpenseRealized = filtered
      .filter((t) => t.type === 'expense' && t.status === 'completed')
      .reduce((s, t) => s + t.amount, 0);
    const periodBalance =
      totalIncomeRealized + totalIncomePredicted - (totalExpenseRealized + totalExpensePredicted);
    const currentBalance = accounts.reduce((s, a) => s + a.currentBalance, 0);
    return {
      totalIncomePredicted,
      totalIncomeRealized,
      totalExpensePredicted,
      totalExpenseRealized,
      periodBalance,
      currentBalance,
    };
  }, [getFilteredTransactions, accounts]);

  const value = useMemo(
    () => ({
      accounts,
      categories,
      transactions,
      costCenters,
      counterparties,
      filters,
      isLoading,
      isRefetching,
      setFilters,
      addTransaction,
      updateTransaction,
      deleteTransaction,
      deleteRecurringTransaction,
      updateRecurringTransaction,
      markAsCompleted,
      addAccount,
      updateAccount,
      deleteAccount,
      addCategory,
      updateCategory,
      deleteCategory,
      addCostCenter,
      updateCostCenter,
      deleteCostCenter,
      addCounterparty,
      updateCounterparty,
      deleteCounterparty,
      getChartData,
      getDashboardSummary,
      getFilteredTransactions,
      getCategoryById,
      getAccountById,
      getCostCenterById,
      getCategoryByName,
      getCostCenterByName,
      importBatch,
      reconcileMatches,
      approveTransactions,
      rejectTransactions,
      bulkMarkCompleted,
      bulkMarkPending,
      bulkUpdateCostCenter,
      bulkUpdateCounterparty,
      bulkDelete,
      closeCreditCardInvoice,
      refetchAllData,
    }),
    [
      accounts,
      categories,
      transactions,
      costCenters,
      counterparties,
      filters,
      isLoading,
      isRefetching,
      setFilters,
      addTransaction,
      updateTransaction,
      deleteTransaction,
      deleteRecurringTransaction,
      updateRecurringTransaction,
      markAsCompleted,
      addAccount,
      updateAccount,
      deleteAccount,
      addCategory,
      updateCategory,
      deleteCategory,
      addCostCenter,
      updateCostCenter,
      deleteCostCenter,
      addCounterparty,
      updateCounterparty,
      deleteCounterparty,
      getChartData,
      getDashboardSummary,
      getFilteredTransactions,
      getCategoryById,
      getAccountById,
      getCostCenterById,
      getCategoryByName,
      getCostCenterByName,
      importBatch,
      reconcileMatches,
      approveTransactions,
      rejectTransactions,
      bulkMarkCompleted,
      bulkMarkPending,
      bulkUpdateCostCenter,
      bulkUpdateCounterparty,
      bulkDelete,
      closeCreditCardInvoice,
      refetchAllData,
    ],
  );

  return <FinanceContext.Provider value={value}>{children}</FinanceContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useFinance() {
  const ctx = useContext(FinanceContext);
  if (ctx === undefined) throw new Error('useFinance must be used within a FinanceProvider');
  return ctx;
}
